import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {
  goToConsent,
  oauthErrorText,
  stashOAuthDraft,
  useGrantResources,
  useOAuthGrants,
  useOAuthProviders,
  useStartOAuth,
} from 'api/oauth';
import { extractApiError } from 'api/errors';

const STATUS_COLOR = { active: 'success', broken: 'warning', revoked: 'default' };

const accountLabel = (grant) => grant.external_login || grant.external_account_id;
const lacksScopes = (grant) => (grant.missing_scopes || []).length > 0;

/**
 * The connected-account half of a connection form, driven by the connector
 * type's `auth` declaration: a Connect button, a picker over the tenant's live
 * accounts that hold the type's scopes (the rest listed apart with a reconnect)
 * and, with `resource_kind`, a picker that fills `resource_field`.
 */
export default function OAuthConnectField({
  auth,
  connectorTypeSlug,
  grantId,
  onGrantChange,
  resourceValue,
  onResourceChange,
  returnTo,
  draft,
  errorCode,
}) {
  const { t: translate } = useTranslation();
  const provider = auth?.provider;
  const { data: providers, isLoading: providersLoading } = useOAuthProviders();
  const { data: grants, isLoading: grantsLoading } = useOAuthGrants(provider, connectorTypeSlug);
  const start = useStartOAuth();

  const providerInfo = useMemo(
    () => (providers || []).find((p) => p.key === provider),
    [providers, provider]
  );
  const liveGrants = useMemo(
    () => (grants || []).filter((g) => g.status === 'active'),
    [grants]
  );
  const eligible = useMemo(() => liveGrants.filter((g) => !lacksScopes(g)), [liveGrants]);
  const lacking = useMemo(() => liveGrants.filter(lacksScopes), [liveGrants]);
  const selected = (grants || []).find((g) => g.public_id === grantId) || null;
  const selectedLacks = !!selected && lacksScopes(selected);
  const selectedEligible = eligible.some((g) => g.public_id === grantId);
  // The type's declared scopes, else the adapter's defaults.
  const requiredScopes = auth?.scopes?.length ? auth.scopes : providerInfo?.default_scopes || [];

  const connect = async () => {
    stashOAuthDraft(provider, draft);
    const data = await start.mutateAsync({ provider, connectorTypeSlug, returnTo });
    goToConsent(data.authorize_url);
  };

  if (providersLoading) {
    return <CircularProgress size={18} />;
  }

  const name = providerInfo?.name || provider;
  const reconnectButton = (
    <Button
      size="small"
      color="inherit"
      startIcon={start.isPending ? <CircularProgress size={12} color="inherit" /> : <AutorenewIcon />}
      onClick={connect}
      disabled={start.isPending}
    >{translate('shell.reconnect')}</Button>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {translate('shell.oauthAccount', { name })}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {translate('shell.oauthHint', { name })}
          {requiredScopes.length > 0 && translate('shell.oauthScopes', { scopes: requiredScopes.join(', ') })}
        </Typography>
      </Box>

      {errorCode && <Alert severity="warning">{oauthErrorText(errorCode)}</Alert>}
      {start.isError && (
        <Alert severity="error">
          {extractApiError(start.error, translate('shell.oauthStartFailed'))}
        </Alert>
      )}

      {providerInfo && !providerInfo.enabled ? (
        <Alert severity="info">
          {translate('shell.oauthDisabled', { name })}
        </Alert>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {grantsLoading ? (
              <CircularProgress size={18} />
            ) : (
              (eligible.length > 0 || grantId) && (
                <TextField
                  select
                  size="small"
                  label={translate('shell.connectedAccount')}
                  value={grantId || ''}
                  onChange={(e) => onGrantChange(e.target.value || null)}
                  sx={{ minWidth: 260 }}
                  helperText={grantId ? '' : translate('shell.chooseAccount')}
                >
                  {grantId && !selectedEligible && (
                    <MenuItem value={grantId} disabled>
                      {selected ? accountLabel(selected) : translate('shell.connectedAccount')}
                    </MenuItem>
                  )}
                  <MenuItem value="">
                    <em>{translate('shell.enterTokenManually')}</em>
                  </MenuItem>
                  {eligible.map((g) => (
                    <MenuItem key={g.public_id} value={g.public_id}>
                      {accountLabel(g)}
                    </MenuItem>
                  ))}
                </TextField>
              )
            )}
            <Button
              variant={grantId ? 'outlined' : 'contained'}
              startIcon={start.isPending ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
              onClick={connect}
              disabled={start.isPending}
            >
              {liveGrants.length > 0 ? translate('shell.connectAnotherAccount', { name }) : translate('shell.connectProvider', { name })}
            </Button>
          </Box>

          {lacking.length > 0 && !selectedLacks && (
            <Alert severity="info" action={reconnectButton}>
              {lacking.map((g) => (
                <Typography key={g.public_id} variant="body2">
                  {translate('shell.accountLacksScopes', { account: accountLabel(g), scopes: g.missing_scopes.join(', ') })}
                </Typography>
              ))}
              <Typography variant="body2">{translate('shell.reconnectHint')}</Typography>
            </Alert>
          )}
        </>
      )}

      {selected && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={accountLabel(selected)}
            color={selectedLacks ? 'warning' : STATUS_COLOR[selected.status] || 'default'}
          />
          <Typography variant="caption" color="text.secondary">
            {selected.scopes?.length ? translate('shell.accessScopes', { scopes: selected.scopes.join(', ') }) : ''}
          </Typography>
        </Box>
      )}

      {selectedLacks && (
        <Alert severity="warning" action={reconnectButton}>
          {translate('shell.selectedLacksScopes', { account: accountLabel(selected), scopes: selected.missing_scopes.join(', ') })}
        </Alert>
      )}

      {selected?.status === 'active' && !selectedLacks && auth?.resource_kind && (
        <ResourcePicker
          grantId={selected.public_id}
          kind={auth.resource_kind}
          field={auth.resource_field}
          value={resourceValue}
          onChange={onResourceChange}
        />
      )}
    </Box>
  );
}

/** A select over the account's resources, filling one config field. */
function ResourcePicker({ grantId, kind, field, value, onChange }) {
  const { t: translate } = useTranslation();
  const { data, isLoading, error } = useGrantResources(grantId, kind);
  const resources = data?.resources || [];
  const resourceLabels = { counter: 'shell.resourceCounter', project: 'shell.resourceProject', property: 'shell.resourceProperty', account: 'shell.resourceAccount' };
  const label = translate(resourceLabels[kind] || 'shell.resourceProperty', { defaultValue: kind.replace(/_/g, ' ') });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          {translate('shell.loadingResources', { label })}
        </Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="warning">
        {extractApiError(error, translate('shell.listResourcesFailed', { label }))} {translate('shell.enterResourceManually', { field })}
      </Alert>
    );
  }
  if (data?.error) {
    return (
      <Alert severity="warning">
        {translate('shell.providerNoResponse', { error: data.error, field })}
      </Alert>
    );
  }
  if (resources.length === 0) {
    return (
      <Alert severity="info">
        {translate('shell.noResources', { label, field })}
      </Alert>
    );
  }
  const known = resources.some((r) => String(r.id) === String(value ?? ''));
  return (
    <TextField
      select
      size="small"
      label={label.charAt(0).toUpperCase() + label.slice(1)}
      value={known ? String(value) : ''}
      onChange={(e) => onChange(e.target.value)}
      helperText={translate('shell.fillsField', { field })}
      sx={{ maxWidth: 420 }}
    >
      {resources.map((r) => (
        <MenuItem key={r.id} value={String(r.id)}>
          {r.name ? `${r.name} — ${r.id}` : r.id}
          {r.site ? ` (${r.site})` : ''}
        </MenuItem>
      ))}
    </TextField>
  );
}
